import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/dashboard/page-header";
import {
  DollarSign,
  Users,
  CreditCard,
  Activity,
  Menu,
  Bell,
  Search,
  Calendar,
  BarChart2,
  PieChart,
  TrendingUp,
  Settings,
  HelpCircle,
  User,
  ArrowRight,
  BarChart,
  LineChart,
  ChevronDown,
  Layers,
  Zap,
  Gauge,
  X,
  Plus,
  Download,
  Filter,
  RefreshCw,
  MoreHorizontal,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  MessageSquare,
  Clock,
  Star
} from "lucide-react";

import { OverviewChart } from "../components/dashboard/overview-chart-simple";
import { RecentSales } from "../components/dashboard/recent-sales";
import { AreaChart } from "../components/dashboard/area-chart";
import { DonutChart } from "../components/dashboard/donut-chart";
import { ActivityList } from "../components/dashboard/activity-list";
import { DashboardLayout } from "../components/dashboard/dashboard-layout";
import ProtectedRoute from "../components/ProtectedRoute";
import { useAuth } from "../context/AuthContext";
import apiClient from "../services/api";

import "../styles/animations.css";

// Types for dashboard data
interface DashboardStats {
  totalReviews: number;
  responseRate: number;
  avgRating: number;
  avgResponseTime: number;
  avgResponseTimeHours: number;
  totalReviewsChange: number;
  responseRateChange: number;
  avgResponseTimeChange: number;
  avgRatingChange: number;
  recentActivity: {
    newReviews: number;
    responsesGenerated: number;
    responsesSent: number;
  };
  platformBreakdown: Record<string, any>;
  statusBreakdown: {
    pending: number;
    draft: number;
    sent: number;
  };
  totalCredits: number;
  usedCredits: number;
  availableCredits: number;
}

function DashboardContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [activeChart, setActiveChart] = useState("reviews");
  const [animateStats, setAnimateStats] = useState(false);
  const [isChartChanging, setIsChartChanging] = useState(false);

  // Date range state for main dashboard
  const [selectedDateRange, setSelectedDateRange] = useState<{ from: Date; to?: Date }>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6); // Last 7 days
    return { from: start, to: end };
  });

  // Date range state for transactions section (simple dropdown)
  const [transactionDateRange, setTransactionDateRange] = useState("Last 7 days");
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard data with date range
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Format dates for API
      const fromDate = selectedDateRange.from.toISOString().split('T')[0];
      const toDate = selectedDateRange.to ? selectedDateRange.to.toISOString().split('T')[0] : fromDate;

      console.log('📊 Fetching dashboard data for date range:', { fromDate, toDate });

      // Send date range as query parameters
      const response = await apiClient.get(`/api/dashboard/stats?from=${fromDate}&to=${toDate}`);

      if (response.data.success) {
        setDashboardStats(response.data.data);
        console.log('✅ Dashboard data loaded:', response.data.data);
      } else {
        throw new Error(response.data.error || 'Failed to fetch dashboard data');
      }
    } catch (error: any) {
      console.error('❌ Error fetching dashboard data:', error);
      setError(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Trigger animations and fetch data when component mounts or date range changes
  useEffect(() => {
    setAnimateStats(true);
    fetchDashboardData();
  }, [selectedDateRange]);



  // Refresh data function
  const handleRefresh = () => {
    fetchDashboardData();
  };

  // Function to handle stat card clicks to change the active chart with synchronized transitions
  const handleStatCardClick = (chartType: string) => {
    if (chartType === activeChart) return;

    // Start transition animation
    setIsChartChanging(true);

    // Single timeout synchronized with CSS transition duration
    setTimeout(() => {
      setActiveChart(chartType);
      setIsChartChanging(false);
    }, 350);
  };

  // Get current date for display
  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">
        <DashboardLayout>
          <div className="flex-1 overflow-hidden h-full">
            <main className="p-4 md:p-6 max-w-7xl mx-auto">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto mb-4"></div>
                  <p className="text-gray-500 dark:text-gray-400">Loading dashboard...</p>
                </div>
              </div>
            </main>
          </div>
        </DashboardLayout>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">
        <DashboardLayout>
          <div className="flex-1 overflow-hidden h-full">
            <main className="p-4 md:p-6 max-w-7xl mx-auto">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="text-red-500 mb-4">
                    <X className="h-12 w-12 mx-auto" />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
                  <button
                    onClick={handleRefresh}
                    className="px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </main>
          </div>
        </DashboardLayout>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">

      <div className="flex flex-col md:flex-row">
        {/* Main Content */}
          <DashboardLayout>
        <div className="flex-1 overflow-hidden h-full">
          <main className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* Dashboard Header */}
            <PageHeader
              title={`Welcome back, ${user?.name || user?.email?.split('@')[0] || 'User'}!`}
              description="Here's what's happening with your business today."
              tabs={[
                { id: "overview", label: "Overview" },
                { id: "analytics", label: "Analytics", disabled: true },
                { id: "reports", label: "Reports", disabled: true },
                { id: "notifications", label: "Notifications", disabled: true },
              ]}
              defaultTab="overview"
              onTabChange={(tabId) => setActiveTab(tabId)}
              onRefresh={handleRefresh}
              showDatePicker={true}
              selectedDateRange={selectedDateRange}
              onDateRangeChange={(range) => {
                if (range && typeof range === 'object' && 'from' in range) {
                  setSelectedDateRange(range);
                  // Refresh data with new date range
                  handleRefresh();
                }
              }}
            />

            {/* Stats Cards */}
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-6 md:mb-8">
              <div
                className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md border-transition transform hover:-translate-y-2 cursor-pointer ${activeChart === 'reviews' ? 'active' : ''} ${animateStats ? 'animate-fadeIn delay-100' : 'opacity-0'}`}
                onClick={() => handleStatCardClick('reviews')}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Reviews</h3>
                    <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover-rotate">
                      <MessageSquare className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold">{dashboardStats?.totalReviews?.toLocaleString() || '0'}</span>
                    <span className="ml-2 text-sm font-medium flex items-center text-gray-700 dark:text-gray-300">
                      <ArrowUpRight className="h-3 w-3 mr-1" />
                      +{dashboardStats?.totalReviewsChange || 0}%
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Across all platforms</div>
                  <div className="mt-4 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gray-900 dark:bg-gray-100 rounded-full animate-progress" style={{ width: `${Math.min(100, (dashboardStats?.totalReviews || 0) / 10)}%` }}></div>
                  </div>
                </div>
              </div>

              <div
                className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md border-transition transform hover:-translate-y-2 cursor-pointer ${activeChart === 'response' ? 'active' : ''} ${animateStats ? 'animate-fadeIn delay-200' : 'opacity-0'}`}
                onClick={() => handleStatCardClick('response')}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Response Rate</h3>
                    <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover-rotate">
                      <Activity className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold">{dashboardStats?.responseRate?.toFixed(1) || '0'}%</span>
                    <span className="ml-2 text-sm font-medium flex items-center text-gray-700 dark:text-gray-300">
                      <ArrowUpRight className="h-3 w-3 mr-1" />
                      +{dashboardStats?.responseRateChange || 0}%
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Reviews with responses</div>
                  <div className="mt-4 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gray-900 dark:bg-gray-100 rounded-full animate-progress" style={{ width: `${dashboardStats?.responseRate || 0}%` }}></div>
                  </div>
                </div>
              </div>

              <div
                className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md border-transition transform hover:-translate-y-2 cursor-pointer ${activeChart === 'rating' ? 'active' : ''} ${animateStats ? 'animate-fadeIn delay-300' : 'opacity-0'}`}
                onClick={() => handleStatCardClick('rating')}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Average Rating</h3>
                    <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover-rotate">
                      <Star className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold">{dashboardStats?.avgRating?.toFixed(1) || '0.0'}</span>
                    <span className="ml-2 text-sm font-medium flex items-center text-gray-700 dark:text-gray-300">
                      <ArrowUpRight className="h-3 w-3 mr-1" />
                      +{dashboardStats?.avgRatingChange || 0}%
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Out of 5 stars</div>
                  <div className="mt-4 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gray-900 dark:bg-gray-100 rounded-full animate-progress" style={{ width: `${((dashboardStats?.avgRating || 0) / 5) * 100}%` }}></div>
                  </div>
                </div>
              </div>

              <div
                className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md border-transition transform hover:-translate-y-2 cursor-pointer ${activeChart === 'time' ? 'active' : ''} ${animateStats ? 'animate-fadeIn delay-400' : 'opacity-0'}`}
                onClick={() => handleStatCardClick('time')}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Response Time</h3>
                    <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover-rotate">
                      <Clock className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold">{dashboardStats?.avgResponseTimeHours?.toFixed(1) || '0.0'}h</span>
                    <span className="ml-2 text-sm font-medium flex items-center text-gray-700 dark:text-gray-300">
                      <ArrowDownRight className="h-3 w-3 mr-1" />
                      -{dashboardStats?.avgResponseTimeChange || 0}%
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Average response time</div>
                  <div className="mt-4 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gray-900 dark:bg-gray-100 rounded-full animate-progress" style={{ width: `${Math.max(10, 100 - ((dashboardStats?.avgResponseTimeHours || 0) * 10))}%` }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Section - Dynamic based on selected stat card */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 md:mb-8">
              <div className={`lg:col-span-2 bg-white dark:bg-gray-900 rounded-xl shadow-sm p-5 border border-gray-200 dark:border-gray-800 hover:shadow-md border-transition ${animateStats ? 'animate-fadeIn delay-200' : 'opacity-0'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <LineChart className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <h3 className={`text-lg font-bold ${isChartChanging ? 'opacity-0 transform translate-y-0.0625 scale-98' : 'opacity-100 transform translate-y-0 scale-100'} transition-all duration-350 chart-transition`}>
                      {activeChart === 'reviews' && 'Review Trends'}
                      {activeChart === 'response' && 'Response Rate Trends'}
                      {activeChart === 'rating' && 'Rating Performance'}
                      {activeChart === 'time' && 'Response Time Analysis'}
                    </h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className={`flex items-center ${isChartChanging ? 'opacity-0 transform translate-x-2' : 'opacity-100 transform translate-x-0'} transition-all duration-300 ease-in-out delay-100`}>
                      <div className="h-3 w-3 rounded-full bg-gray-900 dark:bg-gray-100 mr-1"></div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {activeChart === 'reviews' && 'Total Reviews'}
                        {activeChart === 'response' && 'Response Rate'}
                        {activeChart === 'rating' && 'Average Rating'}
                        {activeChart === 'time' && 'Response Time'}
                      </span>
                    </div>
                    <div className={`flex items-center ${isChartChanging ? 'opacity-0 transform translate-x-2' : 'opacity-100 transform translate-x-0'} transition-all duration-100 ease-in-out delay-100`}>
                      <div className="h-3 w-3 rounded-full bg-gray-400 dark:bg-gray-600 mr-1"></div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {activeChart === 'reviews' && 'Target'}
                        {activeChart === 'response' && 'Goal'}
                        {activeChart === 'rating' && 'Benchmark'}
                        {activeChart === 'time' && 'Target Time'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 ml-auto">
                     <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 transform active:scale-95">
                        <Download className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      </button>
                      <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 transform active:scale-95">
                        <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      </button>
                      <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 transform active:scale-95">
                        <MoreHorizontal className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`h-[300px] w-full overflow-hidden chart-content-transition ${isChartChanging ? 'chart-exit chart-exit-active' : 'chart-enter chart-enter-active'}`}>
                  <AreaChart activeChart={activeChart} dashboardStats={dashboardStats} />
                </div>
              </div>

              <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm p-5 border border-gray-200 dark:border-gray-800 hover:shadow-md border-transition ${animateStats ? 'animate-fadeIn delay-300' : 'opacity-0'}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <h3 className={`text-lg font-bold ${isChartChanging ? 'opacity-0 transform translate-y-0.0625' : 'opacity-100 transform translate-y-0'} transition-all duration-350`}>
                      {activeChart === 'reviews' && 'Platform Distribution'}
                      {activeChart === 'response' && 'Response Status'}
                      {activeChart === 'rating' && 'Rating Distribution'}
                      {activeChart === 'time' && 'Response Time Breakdown'}
                    </h3>
                  </div>
                  <button className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-all duration-250 hover:underline flex items-center gap-1 btn-transition">
                    <span>Details</span>
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
                <div className={`h-[300px] w-full overflow-hidden chart-content-transition ${isChartChanging ? 'chart-exit chart-exit-active' : 'chart-enter chart-enter-active'}`}>
                  {activeChart === 'reviews' && (
                    <DonutChart
                      data={dashboardStats?.platformBreakdown ? Object.entries(dashboardStats.platformBreakdown).map(([platform, data]: [string, any], index) => ({
                        label: platform.charAt(0).toUpperCase() + platform.slice(1),
                        value: data.total || data.count || 0,
                        color: ['#111827', '#1F2937', '#374151', '#4B5563', '#6B7280', '#9CA3AF'][index % 6]
                      })) : undefined}
                      title="Platform Distribution"
                      centerText="Total Reviews"
                      centerValue={dashboardStats?.totalReviews?.toLocaleString() || "0"}
                    />
                  )}
                  {activeChart === 'response' && (
                    <DonutChart
                      data={dashboardStats?.statusBreakdown ? [
                        { label: 'Sent', value: dashboardStats.statusBreakdown.sent, color: '#111827' },
                        { label: 'Draft', value: dashboardStats.statusBreakdown.draft, color: '#374151' },
                        { label: 'Pending', value: dashboardStats.statusBreakdown.pending, color: '#6B7280' }
                      ] : undefined}
                      title="Response Status"
                      centerText="Response Rate"
                      centerValue={`${dashboardStats?.responseRate?.toFixed(1) || '0'}%`}
                    />
                  )}
                  {activeChart === 'rating' && (
                    <DonutChart
                      data={[
                        { label: '5 Stars', value: 40, color: '#111827' },
                        { label: '4 Stars', value: 30, color: '#1F2937' },
                        { label: '3 Stars', value: 20, color: '#374151' },
                        { label: '2 Stars', value: 7, color: '#4B5563' },
                        { label: '1 Star', value: 3, color: '#6B7280' }
                      ]}
                      title="Rating Distribution"
                      centerText="Avg Rating"
                      centerValue={`${dashboardStats?.avgRating?.toFixed(1) || '0.0'}`}
                    />
                  )}
                  {activeChart === 'time' && (
                    <DonutChart
                      data={[
                        { label: '< 1 Hour', value: 25, color: '#111827' },
                        { label: '1-6 Hours', value: 35, color: '#1F2937' },
                        { label: '6-24 Hours', value: 25, color: '#374151' },
                        { label: '> 24 Hours', value: 15, color: '#4B5563' }
                      ]}
                      title="Response Time Breakdown"
                      centerText="Avg Time"
                      centerValue={`${dashboardStats?.avgResponseTimeHours?.toFixed(1) || '0.0'}h`}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Additional Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 md:mb-8">
              <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm p-5 border border-gray-200 dark:border-gray-800 hover:shadow-md border-transition card-hover ${animateStats ? 'animate-fadeIn delay-400' : 'opacity-0'}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Gauge className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <h3 className={`text-lg font-bold ${isChartChanging ? 'opacity-0 transform translate-y-0.25 scale-98' : 'opacity-100 transform translate-y-0 scale-100'} transition-all duration-350 chart-transition`}>
                      {activeChart === 'reviews' && 'Review Metrics'}
                      {activeChart === 'response' && 'Response Analytics'}
                      {activeChart === 'rating' && 'Rating Overview'}
                      {activeChart === 'time' && 'Response Time Analysis'}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                      <ArrowUpRight className="h-3 w-3 mr-1" />
                      +5.2%
                    </span>
                    <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 transform active:scale-95">
                      <MoreHorizontal className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                </div>
                <div className={`h-[300px]  chart-content-transition ${isChartChanging ? 'chart-exit chart-exit-active' : 'chart-enter chart-enter-active'}`}>
                  <OverviewChart activeChart={activeChart} dashboardStats={dashboardStats} />
                </div>
              </div>

              <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm p-5 border border-gray-200 dark:border-gray-800 hover:shadow-md border-transition card-hover ${animateStats ? 'animate-fadeIn delay-500' : 'opacity-0'}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <h3 className={`text-lg font-bold ${isChartChanging ? 'opacity-0 transform translate-y-0.25 scale-98' : 'opacity-100 transform translate-y-0 scale-100'} transition-all duration-350 chart-transition`}>
                      {activeChart === 'reviews' && 'Review Activity'}
                      {activeChart === 'response' && 'Response Activity'}
                      {activeChart === 'rating' && 'Rating Activity'}
                      {activeChart === 'time' && 'Response Activity'}
                    </h3>
                  </div>
                  <button
                    onClick={() => navigate('/inbox')}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-all duration-250 hover:underline flex items-center gap-1 btn-transition"
                  >
                    <span>View All</span>
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
                <div className={`h-[300px] overflow-auto chart-content-transition ${isChartChanging ? 'chart-exit chart-exit-active' : 'chart-enter chart-enter-active'}`}>
                  <ActivityList />
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm p-5 border border-gray-200 dark:border-gray-800 hover:shadow-md border-transition card-hover ${animateStats ? 'animate-fadeIn delay-600' : 'opacity-0'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 over">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <h3 className={`text-lg font-bold ${isChartChanging ? 'opacity-0 transform translate-y-0.25 scale-98' : 'opacity-100 transform translate-y-0 scale-100'} transition-all duration-350 chart-transition`}>
                    {activeChart === 'reviews' && 'Recent Transactions'}
                    {activeChart === 'response' && 'Response Transactions'}
                    {activeChart === 'rating' && 'Rating Transactions'}
                    {activeChart === 'time' && 'Recent Transactions'}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <select
                      value={transactionDateRange}
                      onChange={(e) => {
                        setTransactionDateRange(e.target.value);
                        // Refresh transactions with new date range
                        handleRefresh();
                      }}
                      className="appearance-none pl-3 pr-8 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700 transition-all duration-200"
                    >
                      <option value="Last 7 days">Last 7 days</option>
                      <option value="Last 30 days">Last 30 days</option>
                      <option value="Last 90 days">Last 90 days</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
                  </div>
                  <button
                    onClick={() => navigate('/transactions')}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 transform active:scale-95 border border-gray-200 dark:border-gray-700 btn-transition hover:shadow-sm"
                  >
                    <span>View All</span>
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <div>
                <RecentSales />
              </div>
            </div>

            {/* Action Buttons - Fixed position with animations */}
            <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-10">
              <button className="h-12 w-12 rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-103 active:scale-98 btn-ripple" style={{ animation: 'floatButton 3s cubic-bezier(0.4, 0, 0.2, 1) infinite' }}>
                <Plus className="h-6 w-6" />
              </button>
              <button className="h-10 w-10 rounded-full bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-180 transform hover:scale-102 active:scale-98 border border-gray-200 dark:border-gray-700" style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>




          </main>
        </div>
          </DashboardLayout >
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <ProtectedRoute requireOnboarding={true}>
      <DashboardContent />
    </ProtectedRoute>
  );
}
